import React from 'react';
import {Row, Col, Pagination} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';

interface PaginationComponentProps {
    handlePageChange: (offset: number) => void;
    totalRecords: number;
    recordsPerPage: number;
    offset: number;
}

export const CustomPagination: React.FC<PaginationComponentProps> = ({
    handlePageChange,
    totalRecords,
    recordsPerPage,
    offset,
}) => {
    const {t} = useTranslation();

    const totalPages = Math.ceil(totalRecords / recordsPerPage);
    const currentPage = Math.floor(offset / recordsPerPage);

    const items = [];

    const createPaginationItem = (pageIndex: number) => (
        <Pagination.Item
            key={pageIndex}
            active={pageIndex === currentPage}
            onClick={() => handlePageChange(pageIndex * recordsPerPage)}>
            {pageIndex + 1}
        </Pagination.Item>
    );

    if (totalPages <= 5) {
        for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
            items.push(createPaginationItem(pageIndex));
        }
    } else {
        items.push(createPaginationItem(0)); // First page

        if (currentPage > 2) {
            items.push(<Pagination.Ellipsis key="ellipsis-start" />);
        }

        for (
            let pageIndex = Math.max(1, currentPage - 1);
            pageIndex <= Math.min(currentPage + 1, totalPages - 2);
            pageIndex++
        ) {
            items.push(createPaginationItem(pageIndex));
        }

        if (currentPage < totalPages - 3) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" />);
        }

        items.push(createPaginationItem(totalPages - 1)); // Last page
    }

    const start = totalRecords === 0 ? 0 : offset + 1;
    const end = Math.min(offset + recordsPerPage, totalRecords);

    return (
        <Row className="dashboard-pagination d-flex justify-content-between align-items-center">
            <Col
                xs={12}
                sm="auto"
                className="d-flex justify-center text-center">
                {t('pagination.navigation', {
                    from: start,
                    to: end,
                    of: totalRecords,
                })}
            </Col>
            <Col xs={12} sm="auto" className="d-flex justify-center">
                <Pagination>
                    <Pagination.Prev
                        disabled={currentPage <= 0}
                        onClick={() =>
                            handlePageChange(offset - recordsPerPage)
                        }
                    />
                    {items}
                    <Pagination.Next
                        disabled={
                            totalPages === 0 || currentPage >= totalPages - 1
                        }
                        onClick={() =>
                            handlePageChange(offset + recordsPerPage)
                        }
                    />
                </Pagination>
            </Col>
        </Row>
    );
};
